import { keyframes, Tooltip, useTheme, Text } from '@ltvco/refresh-lib/theme';
import { useRef, useState, useEffect } from 'react';

import {
  StyledConversationListItemText,
  StyledConversationListItem,
} from 'components/DeepSearch/components/ChatHistory/components';

const titleReveal = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to   { clip-path: inset(0 0% 0 0); }
`;

export const ConversationItem = ({
  title,
  isActive,
  onClick,
  animate = false,
}: {
  title: string;
  isActive: boolean;
  onClick: () => void;
  animate?: boolean;
}) => {
  const theme = useTheme();
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [title, isActive]);

  const textElement = (
    <StyledConversationListItemText
      ref={textRef}
      variant="body2"
      isActive={isActive}
      sx={animate ? { animation: `${titleReveal} 0.8s ease-out` } : undefined}
    >
      {title}
    </StyledConversationListItemText>
  );

  const itemContent = (
    <StyledConversationListItem isActive={isActive} onClick={onClick}>
      {textElement}
    </StyledConversationListItem>
  );

  if (isTruncated) {
    const tooltipTitle = (
      <Text
        variant="body2"
        component="span"
        sx={{
          fontWeight: 400,
          color: theme.palette.rats.main,
          whiteSpace: 'nowrap',
          display: 'block',
        }}
      >
        {title}
      </Text>
    );

    return (
      <Tooltip
        title={tooltipTitle}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 'fit-content !important',
              width: 'auto',
              whiteSpace: 'nowrap',
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[3],
              padding: theme.spacing(1, 1.5),
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiTooltip-arrow': {
                color: theme.palette.background.paper,
                '&::before': {
                  border: `1px solid ${theme.palette.divider}`,
                },
              },
            },
          },
        }}
        arrow
        enterDelay={500}
        enterNextDelay={500}
      >
        {itemContent}
      </Tooltip>
    );
  }

  return itemContent;
};
